<script setup lang="ts">
import type { VirtualItem } from "composables/useVirtualList";
import type { ChatMessageGroup } from "./chat.types";
import { chatGroupClasses, chatGroupContentClasses, chatStatusClasses } from "./chat.styles";
import { fileExt, formatSize, linkDomain } from "./utils/attachmentUtils";
import { useChatVirtualItem } from "./useChatVirtualItem";

defineProps<{
  vItem: VirtualItem<ChatMessageGroup>;
  showAvatar: boolean;
  measureItem: (key: string, el: Element | null) => void;
  shouldShowDivider: (index: number) => boolean;
  formatTime: (iso: string) => string;
  formatDivider: (iso: string) => string;
}>();

const {
  dividerCls,
  metaCls,
  avatarCls,
  bubbleLeftCls,
  bubbleRightCls,
  cursorCls,
  typingCls,
  dotCls,
  attImageCls,
  attFileCls,
  attFileExtCls,
  attLinkCls,
  attLinkImgCls,
  attLinkBodyCls,
  loadedImages,
  onImageLoad,
} = useChatVirtualItem();
</script>

<template>
  <div :ref="(el: any) => measureItem(vItem.key, (el as Element) ?? null)">
    <!-- Date divider -->
    <div v-if="shouldShowDivider(vItem.index)" :class="dividerCls" part="divider" class="e-chat-divider">
      {{ formatDivider(vItem.item.sentAt) }}
    </div>

    <!-- Message group -->
    <div :class="chatGroupClasses({ side: vItem.item.side })" part="group" class="e-chat-group">
      <!-- Avatar (left side only) -->
      <div v-if="showAvatar && vItem.item.side === 'left'" :class="avatarCls" part="avatar">
        {{ vItem.item.from.charAt(0).toUpperCase() }}
      </div>

      <!-- Content column -->
      <div :class="chatGroupContentClasses({ side: vItem.item.side })">
        <!-- Meta: sender name + time -->
        <div :class="metaCls" part="meta">
          <span v-if="vItem.item.side === 'left'" class="font-medium">{{ vItem.item.from }}</span>
          <span>{{ formatTime(vItem.item.sentAt) }}</span>
        </div>

        <!-- Bubbles -->
        <div
          v-for="msg in vItem.item.messages"
          :key="msg.id"
          :class="vItem.item.side === 'right' ? bubbleRightCls : bubbleLeftCls"
          part="bubble"
          class="e-chat-bubble"
        >
          <!-- Attachments -->
          <template v-if="msg.attachments?.length">
            <template v-for="(att, ai) in msg.attachments" :key="ai">
              <!-- Image -->
              <div v-if="att.type === 'image'" class="relative">
                <e-skeleton
                  v-show="!loadedImages[`${msg.id}-${ai}`]"
                  variant="rect"
                  class="h-40 w-full rounded-lg"
                ></e-skeleton>
                <img
                  v-show="loadedImages[`${msg.id}-${ai}`]"
                  :src="att.url"
                  :alt="att.alt || ''"
                  :class="attImageCls"
                  part="attachment-image"
                  @load="onImageLoad(`${msg.id}-${ai}`)"
                />
              </div>
              <!-- File -->
              <a
                v-else-if="att.type === 'file'"
                :href="att.url"
                :class="attFileCls"
                part="attachment-file"
                target="_blank"
                rel="noopener noreferrer"
                :download="att.filename"
              >
                <span :class="attFileExtCls">{{ fileExt(att.filename) }}</span>
                <span class="flex min-w-0 flex-col">
                  <span class="truncate font-medium">{{ att.filename }}</span>
                  <span v-if="att.size" class="text-xs text-(--color-foreground)/40">{{ formatSize(att.size) }}</span>
                </span>
              </a>
              <!-- Link preview -->
              <a
                v-else-if="att.type === 'link'"
                :href="att.url"
                :class="attLinkCls"
                part="attachment-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div v-if="att.thumbnail" class="relative">
                  <e-skeleton
                    v-show="!loadedImages[`${msg.id}-${ai}-thumb`]"
                    variant="rect"
                    class="h-32 w-full"
                  ></e-skeleton>
                  <img
                    v-show="loadedImages[`${msg.id}-${ai}-thumb`]"
                    :src="att.thumbnail"
                    :alt="att.title || ''"
                    :class="attLinkImgCls"
                    @load="onImageLoad(`${msg.id}-${ai}-thumb`)"
                  />
                </div>
                <span :class="attLinkBodyCls">
                  <span v-if="att.title" class="truncate text-sm font-medium">{{ att.title }}</span>
                  <span v-if="att.description" class="line-clamp-2 text-xs text-(--color-foreground)/50">{{
                    att.description
                  }}</span>
                  <span class="text-xs text-(--color-primary)">{{ linkDomain(att.url) }}</span>
                </span>
              </a>
            </template>
          </template>
          <!-- Text -->
          <span v-if="msg.msg">{{ msg.msg }}</span>
          <span v-if="msg.streaming && !msg.msg" :class="typingCls" part="typing">
            <span :class="dotCls" />
            <span :class="dotCls" style="animation-delay: 0.15s" />
            <span :class="dotCls" style="animation-delay: 0.3s" />
          </span>
          <span v-else-if="msg.streaming" :class="cursorCls" />
        </div>

        <!-- Status indicator (right side only) -->
        <div
          v-if="vItem.item.status && vItem.item.side === 'right'"
          :class="chatStatusClasses({ status: vItem.item.status })"
          part="status"
        >
          {{ vItem.item.status }}
        </div>
      </div>
    </div>
  </div>
</template>
